import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  BottomSheetContainer,
  Colors,
  createUseBottomSheetModal,
  Flex,
  IconChevronRight,
  TextButton,
  Typography,
  useSafeArea,
  useScreenSize,
} from '@banhala/ui-kit';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withTiming,
} from 'react-native-reanimated';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import { GoodsLikeRecommend } from './components/GoodsLikeRecommend';
import { View } from 'react-native';
import {
  bridge,
  convertDeeplinkToWebLink,
  getWebBaseURL,
} from 'shared/src/utils';
import { ABLY_INFO } from 'shared/src/constant/ablyInfo';
import { PostGoodsActionContext } from './context/PostGoodsActionContext';
import { useLogViewRecommendedModal } from './hooks/useLogViewRecommendedModal';
import { scheduleOnRN } from 'react-native-worklets';

type PostGoodsActionBottomSheetParams = {
  image: string;
  mode: 'like' | 'cart';
  title: {
    highlight: string;
    text: string;
  };
  action: {
    text: string;
    deeplink: string;
  };
  folderSno?: number;
};
export const usePostGoodsActionBottomSheet = createUseBottomSheetModal<
  void,
  PostGoodsActionBottomSheetParams
>(({ param, confirm }) => {
  const { height: screenHeight } = useScreenSize();
  const { bottom } = useSafeArea();

  const initialHeight = screenHeight * 0.6;
  const heightAnim = useSharedValue(0);
  const startHeight = useSharedValue(initialHeight);
  const contentHeight = useSharedValue(0);
  const [enableScroll, setEnableScroll] = useState(false);

  useEffect(() => {
    heightAnim.value = withTiming(initialHeight, { duration: 300 });
  }, [heightAnim, initialHeight]);

  const maxHeight = useMemo(() => {
    return screenHeight * 0.9 + bottom;
  }, [screenHeight, bottom]);

  const style = useAnimatedStyle(() => ({
    height: heightAnim.value + bottom,
  }));

  const gesture = Gesture.Pan()
    .onStart(() => {
      startHeight.value = heightAnim.value;
    })
    .onUpdate(event => {
      const currentMaxHeight = Math.max(
        contentHeight.value + bottom,
        initialHeight,
      );
      const clampedMax = Math.min(currentMaxHeight, maxHeight);
      const newHeight = startHeight.value - event.translationY;
      heightAnim.value = Math.max(0, Math.min(clampedMax, newHeight));
    })
    .onEnd(() => {
      const currentMaxHeight = Math.max(
        contentHeight.value + bottom,
        initialHeight,
      );
      const clampedMax = Math.min(currentMaxHeight, maxHeight);

      if (heightAnim.value >= initialHeight) {
        // 초기 높이보다 크면 → max로 스냅
        heightAnim.value = withTiming(clampedMax, { duration: 200 });
        scheduleOnRN(setEnableScroll, true);
      } else {
        // 초기 높이보다 작으면 → 닫기 애니메이션 후 dismiss
        heightAnim.value = withTiming(0, { duration: 200 });
        // shouldDismiss.value = true;
        scheduleOnRN(confirm);
      }
    });

  useLogViewRecommendedModal(param.mode);

  return (
    <BottomSheetContainer>
      <GestureDetector gesture={gesture}>
        <PostGoodsActionContext.Provider value={{ mode: param.mode }}>
          <Animated.View style={style}>
            <View collapsable={false}>
              <Flex.Center
                padding={{ top: 8, bottom: 4 }}
                borderBottomWidth={0}
                borderColor={'white'}
              >
                <Flex
                  width={40}
                  height={4}
                  backgroundColorValue={Colors.border_primary}
                  borderRadius={2}
                />
              </Flex.Center>
            </View>

            <Flex flex={1}>
              <Flex.Row
                gap={12}
                padding={{ vertical: 8, left: 16, right: 4 }}
                borderColor={'border_tertiary'}
                borderBottomWidth={1}
              >
                <Avatar uri={param.image} size={32} />
                <Flex flex={1} justifyContent={'center'}>
                  <Typography type={'body1'} color={'content_secondary'}>
                    <Typography type={'subtitle1'} color={'content_secondary'}>
                      {param.title.highlight}
                    </Typography>
                    {param.title.text}
                  </Typography>
                </Flex>

                <TextButton
                  color="blue"
                  onPress={() => {
                    if (param.folderSno != null) {
                      bridge.logAnalyticsEvent({
                        name: 'CLICK_FOLDER',
                        analytics: {
                          FOLDER_SNO: param.folderSno.toString(),
                          LANDING_FROM: 'TOAST',
                        },
                      });
                    }
                    bridge.openDeeplink(param.action.deeplink, {
                      webCallback: async () => {
                        const baseURL = await getWebBaseURL();
                        const convertedLink = convertDeeplinkToWebLink(
                          param.action.deeplink,
                        );
                        if (convertedLink === ABLY_INFO.url) {
                          location.href = ABLY_INFO.url;
                          return;
                        }
                        location.href = `${baseURL}${convertedLink}`;
                      },
                    });
                  }}
                  size="small"
                  icon={<IconChevronRight />}
                >
                  {param.action.text}
                </TextButton>
              </Flex.Row>
              <Flex
                onLayout={({ nativeEvent }) => {
                  // sharedValue로 직접 업데이트 (setState 없이)
                  const newHeight = nativeEvent.layout.height;
                  if (newHeight > contentHeight.value) {
                    contentHeight.value = newHeight;
                  }
                }}
              >
                <GoodsLikeRecommend scrollEnabled={enableScroll} />
              </Flex>
            </Flex>
          </Animated.View>
        </PostGoodsActionContext.Provider>
      </GestureDetector>
    </BottomSheetContainer>
  );
});
